/**
 * Geteilte Typen und das Pipeline-Vokabular. Wird von den Netlify Functions und
 * vom Frontend benutzt – dieselbe Wahrheit auf beiden Seiten.
 *
 * Die Stufen sind **nicht geraten**: am 17.08.2026 aus den echten Deals der
 * SIRIUS-CRM-Organisation gelesen. Wenn im CRM eine Stufe umbenannt wird, muss
 * sie hier nachgezogen werden – unbekannte Stufen landen sonst in `sonstige`
 * statt lautlos zu verschwinden.
 */

/** Offene Deal-Stufen in Pipeline-Reihenfolge. */
export const DEAL_STAGES_OPEN = [
  'Infos fehlen/Presale',
  'Termin zur Qualifizierung',
  'Konzept vorbereiten',
  'Konzeptpräsentation',
  'Angebot erstellen',
  'Angebotsbesprechung',
  'Verhandlungen gestartet',
] as const;

export const DEAL_STAGE_WON = 'Gewonnen';
export const DEAL_STAGE_LOST = 'Verloren';

/** Lead-Status: eigenes Vokabular, nicht die Deal-Stufen. */
export const LEAD_STATUS_OPEN = ['Neu', 'Kontaktiert'] as const;
/** Endzustaende – zaehlen NICHT als offen, kommen aber bei converted=false mit. */
export const LEAD_STATUS_TERMINAL = ['Disqualifiziert', 'Junk Lead'] as const;

export const STALE_DAYS = 7;

/**
 * Worauf sich die Stale-Erkennung stützt.
 *
 * `stage-change` ist der Normalfall: das Deal-Feld `Stage_Modified_Time` trägt
 * den Zeitpunkt der letzten **Stufenänderung**. Damit ist die Frage exakt
 * beantwortet, ohne Zohos `Stage_History` (die einen weitreichenden Scope
 * bräuchte) und ohne eigene Mitschrift.
 *
 * `activity` ist der Rückfall für Deals, an denen das Feld leer ist.
 */
export type StaleBasis = 'stage-change' | 'activity';


export type FunnelBucket = {
  stage: string;
  count: number;
  amount: number;
  /** Deals in dieser Stufe **ohne** hinterlegten Betrag. */
  withoutAmount: number;
};

export type StaleDeal = {
  id: string;
  name: string;
  /** Kunde (`Account_Name`) – ohne den ist ein Deal-Name oft nicht zuzuordnen. */
  customer: string | null;
  stage: string;
  /** Tage seit dem Stichtag gemäß `staleBasis`. */
  days: number;
  amount: number;
  /** Deeplink ins CRM, null wenn CRM_BASE fehlt. */
  url: string | null;
};

export type WonLost = {
  count: number;
  amount: number;
  /**
   * Abschlüsse ohne hinterlegten Betrag. Wichtig, weil `Amount` im CRM nur
   * teilweise gefüllt ist: eine Summe ohne diesen Hinweis täuscht Genauigkeit
   * vor, die es nicht gibt.
   */
  withoutAmount: number;
};

/** Ein abgeschlossener Deal für die Listen „gewonnen" und „verloren". */
export type ClosedDeal = {
  id: string;
  name: string;
  customer: string | null;
  /** Betrag, `null` wenn im CRM keiner hinterlegt ist. */
  amount: number | null;
  /** Abschlussdatum (`Closing_Date`). */
  closedOn: string;
  /** Nur bei verlorenen Deals, wenn im CRM erfasst. */
  lostReason?: string | null;
  url: string | null;
};

export type KpiPayload = {
  /** KPI 1: offene Deals nach Stufe, plus offene Leads nach Status. */
  funnelDeals: FunnelBucket[];
  funnelLeads: FunnelBucket[];
  /** Leads mit Status „Neu", die noch nie angefasst wurden (Import-Bestand). */
  leadsUntouched: number;
  /** KPI 2 */
  stale: StaleDeal[];
  staleBasis: StaleBasis;
  /** KPI 3, im Detail: welche Deals gewonnen bzw. verloren wurden (Monat). */
  wonList: ClosedDeal[];
  lostList: ClosedDeal[];
  /** KPI 3 */
  wonWeek: WonLost;
  wonMonth: WonLost;
  lostWeek: WonLost;
  lostMonth: WonLost;
  /** KPI 4 */
  notesWeek: number;
  notesPerActiveDeal: number;
  openDealCount: number;
  openLeadCount: number;
  /** Datenquelle war der Mock, nicht das CRM. */
  mock?: boolean;
};

export type TeamMember = {
  zohoUserId: string;
  name: string;
  isReference: boolean;
};

export type Snapshot = {
  snapshotDate: string;
  zohoUserId: string;
  maName: string;
  payload: KpiPayload;
};

/** Montag der Woche zu einem ISO-Datum (Europe/Berlin-nah, ohne TZ-Drift). */
export function weekStart(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  const shift = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - shift);
  return d.toISOString().slice(0, 10);
}

export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}
